import { motion } from "framer-motion";
import { 
  Shield, 
  TrendingUp, 
  Gift, 
  Coins, 
  MessageSquare, 
  Users,
  Zap,
  Bot,
  Sparkles,
  Ticket,
  Calendar,
  Twitch,
  Youtube,
  Twitter,
  Gamepad2,
  Settings,
  Crown
} from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

const plugins = [
  {
    id: "moderation",
    name: "Moderation",
    description: "Auto-mod, filters, warnings, bans, kicks, and comprehensive logging",
    icon: Shield,
    gradient: "from-red-500 to-orange-500",
    enabled: true,
    category: "moderation"
  },
  {
    id: "leveling",
    name: "Leveling",
    description: "XP system, rank cards, leaderboards, and role rewards",
    icon: TrendingUp,
    gradient: "from-primary to-cyan-400",
    enabled: true,
    category: "engagement"
  },
  {
    id: "welcome",
    name: "Welcome",
    description: "Custom welcome messages, cards, and farewell notifications",
    icon: Users,
    gradient: "from-green-500 to-emerald-400",
    enabled: true,
    category: "automation"
  },
  {
    id: "giveaway",
    name: "Giveaway",
    description: "Host giveaways with customizable requirements and multiple winners",
    icon: Gift,
    gradient: "from-secondary to-pink-400",
    enabled: true,
    category: "engagement"
  },
  {
    id: "economy",
    name: "Economy",
    description: "Virtual currency, daily rewards, gambling, and shop system",
    icon: Coins,
    gradient: "from-discord-yellow to-amber-400",
    enabled: true,
    category: "engagement"
  },
  {
    id: "commands",
    name: "Custom Commands",
    description: "Create unlimited custom commands with variables and actions",
    icon: MessageSquare,
    gradient: "from-blue-500 to-indigo-500",
    enabled: true,
    category: "automation"
  },
  {
    id: "reaction-roles",
    name: "Reaction Roles",
    description: "Self-assignable roles via reactions or button clicks",
    icon: Zap,
    gradient: "from-purple-500 to-violet-500",
    enabled: true,
    category: "automation"
  },
  {
    id: "ai",
    name: "AI Assistant",
    description: "AI-powered text generation, image creation, and chatbots",
    icon: Sparkles,
    gradient: "from-fuchsia-500 to-rose-500",
    enabled: true,
    category: "premium"
  },
  {
    id: "tickets",
    name: "Ticketing",
    description: "Support ticket system with categories and transcripts",
    icon: Ticket,
    gradient: "from-teal-500 to-cyan-500",
    enabled: true,
    category: "moderation"
  },
  {
    id: "birthday",
    name: "Birthdays",
    description: "Birthday tracking, notifications, and special role assignments",
    icon: Calendar,
    gradient: "from-pink-500 to-rose-400",
    enabled: true,
    category: "engagement"
  },
  {
    id: "twitch",
    name: "Twitch Alerts",
    description: "Stream notifications when your favorite streamers go live",
    icon: Twitch,
    gradient: "from-purple-600 to-purple-400",
    enabled: true,
    category: "social"
  },
  {
    id: "youtube",
    name: "YouTube Alerts",
    description: "Notifications for new videos from YouTube channels",
    icon: Youtube,
    gradient: "from-red-600 to-red-400",
    enabled: true,
    category: "social"
  },
  {
    id: "automations",
    name: "Automations",
    description: "Create custom workflows with triggers, conditions, and actions",
    icon: Settings,
    gradient: "from-slate-500 to-slate-400",
    enabled: true,
    category: "automation"
  },
  {
    id: "premium",
    name: "Premium",
    description: "Unlock all features, custom branding, and priority support",
    icon: Crown,
    gradient: "from-amber-500 to-yellow-400",
    enabled: false,
    category: "premium"
  }
];

const categories = [
  { id: "all", label: "All Plugins" },
  { id: "moderation", label: "Moderation" },
  { id: "engagement", label: "Engagement" },
  { id: "automation", label: "Automation" },
  { id: "social", label: "Social" },
  { id: "premium", label: "Premium" }
];

const PluginsShowcase = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [pluginStates, setPluginStates] = useState<Record<string, boolean>>(
    plugins.reduce((acc, p) => ({ ...acc, [p.id]: p.enabled }), {})
  );

  // Demo autoplay: cycle categories and toggles
  useEffect(() => {
    const categoryIds = categories.map((c) => c.id);
    let categoryIndex = 0;

    const categoryInterval = setInterval(() => {
      categoryIndex = (categoryIndex + 1) % categoryIds.length;
      setActiveCategory(categoryIds[categoryIndex]);
    }, 3500);

    const toggleInterval = setInterval(() => {
      const randomPlugin = plugins[Math.floor(Math.random() * plugins.length)];
      if (randomPlugin) {
        setPluginStates((prev) => ({
          ...prev,
          [randomPlugin.id]: !prev[randomPlugin.id],
        }));
      }
    }, 1800);

    return () => {
      clearInterval(categoryInterval);
      clearInterval(toggleInterval);
    };
  }, []);

  const filteredPlugins = plugins.filter(
    p => activeCategory === "all" || p.category === activeCategory
  );

  return (
    <section id="plugins" className="py-20 px-6 relative">
      <div className="container mx-auto max-w-6xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Powerful Plugins</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Customize your server with our extensive collection of plugins. 
            Enable only what you need.
          </p>
        </motion.div>

        {/* Category Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-wrap justify-center gap-3 mb-6"
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => {}}
              disabled
              className={cn(
                "px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-default",
                activeCategory === cat.id
                  ? "bg-gradient-to-r from-primary to-secondary text-white glow-primary"
                  : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground"
              )}
            >
              {cat.label}
            </button>
          ))}
        </motion.div>

        <div className="flex items-center justify-center mb-8">
          <span className="text-xs uppercase tracking-wider px-3 py-1 rounded-full bg-primary/10 text-primary">
            Demo automatica (non interattiva)
          </span>
        </div>

        {/* Plugins Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPlugins.map((plugin, index) => (
            <motion.div
              key={plugin.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="feature-card group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${plugin.gradient} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  <plugin.icon className="w-6 h-6 text-white" />
                </div>
                
                {/* Toggle Switch */}
                <button
                  onClick={() => {
                    setPluginStates(prev => ({
                      ...prev,
                      [plugin.id]: !prev[plugin.id]
                    }));
                  }}
                  className={cn(
                    "plugin-toggle cursor-pointer hover:opacity-80 transition-opacity",
                    pluginStates[plugin.id] ? "enabled" : "disabled"
                  )}
                  aria-label="Demo toggle"
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200",
                      pluginStates[plugin.id] ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>
              
              <h3 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors duration-300">
                {plugin.name}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {plugin.description}
              </p>
              
              {plugin.category === "premium" && (
                <div className="mt-4 inline-flex items-center gap-1 px-3 py-1 bg-amber-500/10 text-amber-400 text-xs font-medium rounded-full">
                  <Crown className="w-3 h-3" />
                  Premium
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PluginsShowcase;
